import type { Config } from './types';
import { ChatAPIs } from './types/constants';
import { isWebRTCAvailable, sdkVersion, isBrowser, isNodeJS, isReactNative } from '@platform';
import config from './config';
import AddressBookService from './AddressBook';
import AuthService from './Auth';
import XMPPChatService from './messaging/xmpp';
import SAMAChatService from './messaging/sama';
import ConferenceClient from './videocalling_conference/ConferenceClient';
import DataService from './Data';
import MeetingsService from './Meetings';
import ProxyService from './Proxy';
import PushNotificationsService from './PushNotifications';
import StorageService from './Storage';
import UsersService from './Users';
import Utils from './Utils';
import WebRTCClient from './videocalling/WebRTCClient';
import WhiteboardService from './Whiteboard';
import LinkPreviewService from './LinkPreview';

export default class ConnectyCube<ChatService extends SAMAChatService | XMPPChatService = XMPPChatService> {
  declare ConnectyCube: typeof ConnectyCube;

  readonly version: string = sdkVersion;
  readonly platform: string = isBrowser ? 'browser' : isNodeJS ? 'node' : isReactNative ? 'react-native' : 'unknown';
  readonly isWebRTCAvailable: boolean = isWebRTCAvailable;

  declare utils: Utils;
  declare service: ProxyService;
  declare auth: AuthService;
  declare users: UsersService;
  declare storage: StorageService;
  declare pushnotifications: PushNotificationsService;
  declare data: DataService;
  declare addressbook: AddressBookService;
  declare chat: ChatService;
  declare meeting: MeetingsService;
  declare whiteboard: WhiteboardService;
  declare linkpreview: LinkPreviewService;
  declare videochat: WebRTCClient;
  declare videochatconference: ConferenceClient;
  declare setSession: AuthService['setSession'];
  declare getSession: AuthService['getSession'];
  declare createSession: AuthService['createSession'];
  declare destroySession: AuthService['destroySession'];
  /**
   * @deprecated ConnectyCube.login(user) is deprecated. Use `ConnectyCube.createSession(user)` or `ConnectyCube.auth.login(user)` instead.
   */
  declare login: AuthService['login'];
  /**
   * @deprecated ConnectyCube.logout() is deprecated. Use `ConnectyCube.destroySession()` or `ConnectyCube.auth.logout()` instead.
   */
  declare logout: AuthService['logout'];

  public init(credentials: Config.Credentials, configMap: Config.Options = {}): void {
    if (configMap && typeof configMap === 'object') {
      config.set(configMap);
    }

    this.utils = Utils;
    this.service = new ProxyService();
    this.auth = new AuthService(this.service);
    this.users = new UsersService(this.service);
    this.storage = new StorageService(this.service);
    this.pushnotifications = new PushNotificationsService(this.service);
    this.data = new DataService(this.service);
    this.addressbook = new AddressBookService(this.service);
    this.chat = (
      config.chat.apiImp === ChatAPIs.SAMA ? new SAMAChatService(this.service) : new XMPPChatService(this.service)
    ) as ChatService;
    this.meeting = new MeetingsService(this.service);
    this.whiteboard = new WhiteboardService(this.service);
    this.linkpreview = new LinkPreviewService(this.service);

    if (isWebRTCAvailable) {
      this.videochat = new WebRTCClient(this.chat, this.service);
      this.chat.webrtcSignalingProcessor = this.videochat.signalingProcessor;
      this.videochatconference = new ConferenceClient(this.service);
    }

    if (credentials.token) {
      config.creds.appId = credentials.appId;
      this.service.setSession({ token: credentials.token });
    } else {
      config.creds.appId = credentials.appId;
      config.creds.authKey = credentials.authKey;
    }

    this.setSession = this.auth.setSession;
    this.getSession = this.auth.getSession;
    this.createSession = this.auth.createSession;
    this.destroySession = this.auth.destroySession;
    this.login = this.auth.login;
    this.logout = this.auth.logout;
  }
}

export { type XMPPChatService as XMPPChat, type SAMAChatService as SAMAChat };
